import { useState } from "react";
import ReactModal from "react-modal";
import { useNavigate } from "react-router-dom";
import PulseLoader from "react-spinners/PulseLoader";
import styled, { useTheme } from "styled-components";
import { IProfessional, IProfessionalSchedulesAvailable } from "../api/getProfessionals";
import registerCheckout from "../api/registerCheckout";
import SelectField from "../components/fields/SelectField";
import { CancelIcon, DoubleArrowRightIcon, VitatCalendarIcon } from "../components/Icons";
import { formatDate, transformDateToType } from "../utils/date-util/dateUtils";
import "./customModal.css";

interface StyledLoadingProps {
	isLoading: boolean;
}

const ModalContentContainer = styled.div`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	display: flex;
	flex-direction: column;
	border: 2px solid #e9ecef;
	width: 90%;
	max-width: 350px;
	background-color: #ffffff;
	border-radius: 8px;
	box-shadow: 0px 4px 4px 1px #0000000a;
`;

const ModalHeader = styled.span`
	font-family: Livvic;
	font-size: 14px;
	font-weight: 600;
	line-height: 22px;
	letter-spacing: 0.005em;
	text-align: left;
	padding: 12px 28px;
	border-bottom: 5px solid ${props => props.theme.colors.greenVitat || "#bfe338"};
	position: relative;

	& > button {
		position: absolute;
		right: 8px;
		background: transparent;
		border: none;
		padding: 2px;
	}
`;

const ModalContent = styled.div`
	padding: 16px 28px;
`;

const ScheduleInfo = styled.p`
	font-family: Livvic;
	font-size: 13px;
	font-weight: 400;
	line-height: 20px;
	letter-spacing: 0.005em;
	text-align: left;
	margin: 24px 0px;
`;

const ProcedureSelectWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	label {
		font-family: Livvic;
		font-size: 13px;
		font-weight: 600;
		line-height: 20px;
		letter-spacing: 0.005em;
		text-align: left;
		color: ${props => props.theme.colors.blackVitat || "#002b49"};
		padding-left: 2px;
	}
`;

const DoneButton = styled.button<StyledLoadingProps>`
	all: unset;
	font-family: Livvic;
	font-size: 13px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0em;
	text-align: center;
	color: #002b49;

	width: 100%;
	padding: 12px 16px;
	box-sizing: border-box;
	position: relative;
	margin-top: 16px;
	border-radius: 8px;
	background-color: ${props => (props.isLoading ? "#cccccc" : props.theme.colors.primary)};
	cursor: pointer;

	:disabled {
		opacity: 0.5;
	}
`;

const ButtonLabel = styled.span<StyledLoadingProps>`
	visibility: ${props => (props.isLoading ? "hidden" : "visible")};
`;

const LoaderWrapper = styled.span`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
`;

const ModalFooter = styled.div`
	border-top: 2px solid #e9ecef;
	box-sizing: border-box;
	padding: 16px 28px;
	display: flex;
	flex-direction: column;
	margin-top: 32px;
`;

const FooterTitle = styled.span`
	font-family: Livvic;
	font-size: 13px;
	font-weight: 600;
	line-height: 20px;
	letter-spacing: 0.005em;
	text-align: left;
	color: ${props => props.theme.colors.brownVitat || props.theme.colors.secondary};
	display: flex;
	align-items: center;
	gap: 6px;
`;

const FooterText = styled.p`
	font-family: Livvic;
	font-size: 11px;
	font-weight: 500;
	line-height: 17px;
	letter-spacing: 0.005em;
	text-align: left;
	color: #868aa7cc;
	color: ${props => props.theme.colors.brownVitat || "#868aa7cc"};
	svg {
		vertical-align: -2px;
		margin-left: 8px;
	}
`;

interface ScheduleInfo {
	professional: string;
	time: string;
}

interface ChooseProcedureModalProps {
	isOpen: boolean;
	handleClose: () => void;
	availableSchedules: Array<IProfessionalSchedulesAvailable>;
	scheduleInfo: ScheduleInfo;
	professional: IProfessional;
}

const ChooseProcedureModal = ({ isOpen, handleClose, availableSchedules, professional, scheduleInfo }: ChooseProcedureModalProps) => {
	const navigate = useNavigate();
	const theme = useTheme();
	const [selectedSchedule, setSelectedSchedule] = useState<IProfessionalSchedulesAvailable>(availableSchedules[0]);
	const [isLoading, setIsLoading] = useState(false);

	const handleLoading = (value: boolean) => {
		setIsLoading(value);
	};

	const handleMakeCheckout = async () => {
		if (!selectedSchedule || isLoading) return;

		try {
			const response = await registerCheckout(selectedSchedule.idHorario, handleLoading);
			if (response) {
				navigate(`/medicos/${professional.codigoProfissional}/checkout/${response.id}?step=0`, { state: selectedSchedule });
				handleClose();
			}
		} catch (error) {
			handleClose();
		}
	};

	const handleOpenSchedule = () => {
		const procedures = professional.procedimentos.map(procedimento => ({
			id: procedimento._id,
			description: procedimento.description,
		}));
		navigate(`/medicos/${professional.codigoProfissional}/agendamentos`, { state: { procedures } });
		handleClose();
	};

	return (
		<ReactModal
			isOpen={isOpen}
			onRequestClose={handleClose}
			overlayClassName="modal-choose-overlay"
			contentElement={(props, children) => <ModalContentContainer onClick={e => e.stopPropagation()}>{children}</ModalContentContainer>}
			ariaHideApp={false}
			shouldCloseOnOverlayClick={!isLoading}
			shouldCloseOnEsc
		>
			<ModalHeader>
				Procedimento
				<button onClick={handleClose}>
					<CancelIcon color="currentColor" size={18} />
				</button>
			</ModalHeader>

			<ModalContent>
				<ScheduleInfo>
					Escolha um procedimento para seu agendamento com {professional.nomeProfissional} para{" "}
					{formatDate("dd/MM, EEEE, 'às' HH:mm'h'", transformDateToType(scheduleInfo.time)).toLocaleLowerCase()}
				</ScheduleInfo>

				<ProcedureSelectWrapper>
					<label htmlFor="select-procedure">Procedimento: *</label>
					<SelectField
						id="select-procedure"
						inputId="select-procedure"
						isSearchable={false}
						placeholder="Selecione"
						value={selectedSchedule}
						options={availableSchedules}
						getOptionLabel={opt => (opt as IProfessionalSchedulesAvailable).procedimento.descricao}
						getOptionValue={opt => (opt as IProfessionalSchedulesAvailable).procedimento._id}
						onChange={opt => setSelectedSchedule(opt as IProfessionalSchedulesAvailable)}
					/>
				</ProcedureSelectWrapper>

				<DoneButton isLoading={isLoading} disabled={!selectedSchedule} onClick={handleMakeCheckout}>
					{isLoading && (
						<LoaderWrapper>
							<PulseLoader color={"#FFFFFF"} loading size={10} />
						</LoaderWrapper>
					)}

					<ButtonLabel isLoading={isLoading}>Pronto!</ButtonLabel>
				</DoneButton>
			</ModalContent>

			<ModalFooter onClick={handleOpenSchedule}>
				<FooterTitle>
					<VitatCalendarIcon size={16} color={"currentColor"} />
					Ver agenda completa
				</FooterTitle>

				<FooterText>
					Existem mais procedimentos disponíveis na agenda completa do profissional
					<DoubleArrowRightIcon size={12} color={theme.colors.brownVitat || theme.colors.secondary} />
				</FooterText>
			</ModalFooter>
		</ReactModal>
	);
};
export default ChooseProcedureModal;
